import {
  AuthUserPermission,
  MpxResponseError,
  OrgUserCreate,
  OrgUserSearchResult,
  OrgUserUpdate,
  Persona,
  UserId,
} from '@cibo/core'
import { FriendlyError } from '@cibo/ui'
import LoadingButton from '@mui/lab/LoadingButton'
import {
  Alert,
  Box,
  Button,
  Collapse,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  Stack,
} from '@mui/material'
import { AxiosError } from 'axios'
import { Field, Form, Formik, FormikHelpers, useFormikContext } from 'formik'
import { Checkbox, TextField } from 'formik-mui'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { useAuth, usePermissions } from '../../hooks'
import { useCreateOrgUser, useOrgUser, useOrganizations, useUpdateOrgUser } from '../../queries'
import { AssignSuborgField } from '../AssignSuborgField'
import { AssignUserControl } from '../AssignUserField'
import { PersonaCards } from '../PersonaCards'

const personasToDisplay: Record<Persona, Persona[]> = {
  grower: [],
  advisor: ['grower', 'advisor'],
  admin: ['grower', 'advisor', 'admin'],
}

interface UserFormProps {
  disabled?: boolean
  onCancel?: () => void
  showStatusField?: boolean
  showPersonaField?: boolean
  isSelf?: boolean
}

interface CreateUserFormProps extends Pick<UserFormProps, 'onCancel'> {
  onComplete?: (user: OrgUserSearchResult) => void
  onError?: (error: AxiosError<MpxResponseError>) => void
}
export const CreateUserForm = ({ onComplete, onError, ...userFormProps }: CreateUserFormProps) => {
  const { t } = useTranslation('@cibo/profile/components/UserForm')
  const createUserValidationSchema = useMemo(
    () =>
      Yup.object().shape({
        email: Yup.string().email().required(t('An email address is required.')),
        givenName: Yup.string().required(t('First name is required.')),
        familyName: Yup.string().required(t('Last name is required.')),
        isActive: Yup.boolean(),
        owningOrgId: Yup.string(),
        advisorId: Yup.number(),
        persona: Yup.string().oneOf(['grower', 'advisor', 'admin']).required(),
      }),
    []
  )

  const createUser = useCreateOrgUser()
  const handleSubmit = useCallback(
    (values: OrgUserCreate, helpers: FormikHelpers<OrgUserCreate>) => {
      createUser
        .mutateAsync(values)
        .then(({ data }) => !!onComplete && onComplete(data.item))
        .catch(error => {
          !!onError && onError(error)
          helpers.setSubmitting(false)
        })
    },
    [onComplete, onError]
  )

  return (
    <Formik<OrgUserCreate>
      initialValues={{
        sendInvite: true,
        email: '',
      }}
      onSubmit={handleSubmit}
      validationSchema={createUserValidationSchema}
      enableReinitialize
    >
      <>
        <Collapse in={createUser.isError}>
          <Box>
            <Alert severity="error">{createUser.error?.response?.data.message}</Alert>
          </Box>
        </Collapse>
        {/* @todo CPD-2517 guard with disabled={can({ useAny: AuthUserPermission.USER_RESEND_INVITE })}> */}
        <UserForm<OrgUserCreate> {...userFormProps} showPersonaField />
      </>
    </Formik>
  )
}

interface UpdateUserFormProps extends CreateUserFormProps {
  userId: UserId
}
export const UpdateUserForm = ({
  onComplete,
  onError,
  userId,
  ...userFormProps
}: UpdateUserFormProps) => {
  const { can } = usePermissions()
  const { t } = useTranslation('@cibo/profile/components/UserForm')
  const updateUserValidationSchema = useMemo(
    () =>
      Yup.object().shape({
        email: Yup.string().email().required(t('An email address is required.')),
        givenName: Yup.string(),
        familyName: Yup.string(),
        isActive: Yup.boolean(),
        owningOrgId: Yup.string(),
        advisorId: Yup.number(),
        persona: Yup.string().oneOf(['grower', 'advisor', 'admin']).required(),
      }),
    []
  )

  const updateUser = useUpdateOrgUser()
  const self = useAuth()
  const { data, error } = useOrgUser(userId)

  const handleSubmit = useCallback(
    (values: OrgUserUpdate, helpers: FormikHelpers<OrgUserUpdate>) => {
      updateUser
        .mutateAsync(values)
        .then(({ item }) => {
          !!onComplete && onComplete(item)
        })
        .catch(error => {
          !!onError && onError(error)
        })
        .finally(() => helpers.setSubmitting(false))
    },
    [onComplete, onError]
  )

  if (error) {
    return <FriendlyError message={t('userFailedToLoad')} />
  }

  const isSelf = self.userId === userId
  const canEdit = can({ useAny: [AuthUserPermission.EDIT_USER] })

  return (
    <Formik<OrgUserUpdate>
      initialValues={{
        email: `${data?.user.email}`,
        givenName: data?.user.givenName,
        familyName: data?.user.familyName,
        account: data?.user.account,
        advisorId: data?.user.advisorId,
        isActive: data?.user.isActive === undefined ? true : data?.user.isActive,
        owningOrgId: data?.user.org?.id,
        persona: data?.user.persona,
      }}
      onSubmit={handleSubmit}
      validationSchema={updateUserValidationSchema}
      enableReinitialize
    >
      <>
        <Collapse in={updateUser.isError}>
          <Box>
            <Alert severity="error">{updateUser.error?.response?.data.message}</Alert>
          </Box>
        </Collapse>
        <UserForm<OrgUserUpdate>
          isSelf={isSelf}
          disabled={!isSelf && !canEdit}
          {...userFormProps}
          showStatusField={!isSelf}
          showPersonaField
        />
      </>
    </Formik>
  )
}

const UserForm = <T extends OrgUserUpdate | OrgUserCreate>({
  disabled,
  onCancel,
  showStatusField,
  showPersonaField,
  isSelf,
}: UserFormProps) => {
  const { t } = useTranslation('@cibo/profile/components/UserForm')
  const { can } = usePermissions()
  const { persona } = useAuth()
  const organziations = useOrganizations()
  const { dirty, isSubmitting, isValid, setFieldValue, values, initialValues, resetForm } =
    useFormikContext<T>()
  const [fullText, setFullText] = useState<string>()

  return (
    <Form>
      <Stack spacing={6}>
        <div>
          <Grid container spacing={2}>
            {showStatusField && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Field
                      id="isActive"
                      component={Checkbox}
                      type="checkbox"
                      name="isActive"
                      disabled={!can({ useAll: AuthUserPermission.EDIT_USER_STATUS })}
                      onChange={(changeArgs: any) =>
                        setFieldValue('isActive', changeArgs.target.checked)
                      }
                    />
                  }
                  label={t('isActive')}
                />
              </Grid>
            )}
            <Grid item sm={6} xs={12}>
              <Stack>
                <InputLabel shrink htmlFor="givenName">
                  {t('givenName')}
                </InputLabel>
                <Field
                  id="givenName"
                  name="givenName"
                  component={TextField}
                  InputProps={{ inputProps: { autoComplete: 'off' } }}
                  disabled={disabled || isSubmitting}
                />
              </Stack>
            </Grid>

            <Grid item sm={6} xs={12}>
              <Stack>
                <InputLabel shrink htmlFor="familyName">
                  {t('familyName')}
                </InputLabel>
                <Field
                  id="familyName"
                  name="familyName"
                  component={TextField}
                  InputProps={{
                    inputProps: { autoComplete: 'off' },
                  }}
                  disabled={disabled || isSubmitting}
                />
              </Stack>
            </Grid>
            <Grid item sm={6} xs={12}>
              <Stack>
                <InputLabel shrink htmlFor="email">
                  {t('email')}
                </InputLabel>
                <Field
                  id="email"
                  name="email"
                  component={TextField}
                  InputProps={{ inputProps: { autoComplete: 'off' } }}
                  // TODO: currently the org info endpoint relies on the email address
                  // as the unique user ID. This needs to be changed and is ticketed, but
                  // at present changing the email address will cause endpoint calls to fail
                  disabled={!!initialValues?.email}
                  // disabled={formDisabled}
                />
              </Stack>
            </Grid>

            <Grid item sm={6} xs={12}>
              <Stack>
                <InputLabel shrink htmlFor="phoneNumber">
                  {t('phoneNumber')}
                </InputLabel>
                <Field
                  id="phoneNumber"
                  name="phoneNumber"
                  component={TextField}
                  InputProps={{
                    inputProps: { autoComplete: 'off' },
                  }}
                  // TODO: disabled b/c currently the org info endpoint does not provide a phone number
                  disabled
                  // disabled={formDisabled}
                />
              </Stack>
            </Grid>
          </Grid>
        </div>

        <div>
          <Grid container spacing={2}>
            {!!organziations.data?.items.length && (
              <Grid item sm={6} xs={12}>
                <Stack>
                  <InputLabel shrink htmlFor="owningOrgId">
                    {t('organization')}
                  </InputLabel>
                  <Field
                    id="owningOrgId"
                    name="owningOrgId"
                    component={(props: any) => (
                      <AssignSuborgField
                        suborgId={values.owningOrgId}
                        setSuborgId={(orgId: string) => {
                          setFieldValue('owningOrgId', orgId, true)
                          setFieldValue('advisorId', undefined, true)
                        }}
                        {...props}
                      />
                    )}
                    disabled={
                      isSelf ||
                      disabled ||
                      isSubmitting ||
                      !can({ useAll: AuthUserPermission.EDIT_USER_ORGANIZATION })
                    }
                  />
                </Stack>
              </Grid>
            )}
            <Grid item sm={6} xs={12}>
              <Stack>
                <InputLabel shrink htmlFor="advisorId">
                  {t('relationshipManager')}
                </InputLabel>
                {/* @todo convert this to a formik field component */}
                <AssignUserControl
                  hideLabel
                  setFullText={setFullText}
                  fullText={fullText}
                  userId={values.advisorId}
                  setUserId={(userId?: UserId) => setFieldValue('advisorId', userId, true)}
                  persona={['advisor', 'admin']}
                  textFieldProps={{
                    helperText: t('relationshipManagerHelper'),
                  }}
                  placeholder={t('searchForAdvisor')}
                  disabled={disabled || isSubmitting}
                  readOnly={isSelf}
                  owningOrgId={values.owningOrgId}
                />
              </Stack>
            </Grid>
            {!isSelf && can({ useAny: AuthUserPermission.VIEW_USER_PERMISSION }) && (
              <Grid item xs={12}>
                {!!persona && showPersonaField && (
                  <PersonaCards
                    disabled={
                      isSelf ||
                      disabled ||
                      isSubmitting ||
                      !can({ useAny: AuthUserPermission.EDIT_USER_PERMISSION })
                    }
                    personas={personasToDisplay[persona]}
                    activePersona={values.persona}
                    onClick={persona => setFieldValue('persona', persona, true)}
                  />
                )}
              </Grid>
            )}
            {!initialValues.email && (
              <Grid xs={12} item>
                <FormControlLabel
                  control={
                    <Field
                      id="sendInvite"
                      component={Checkbox}
                      type="checkbox"
                      name="sendInvite"
                      onChange={(changeArgs: any) =>
                        setFieldValue('sendInvite', changeArgs.target.checked)
                      }
                    />
                  }
                  label={t('sendInviteLabel')}
                  sx={{ color: 'text.secondary' }}
                />
                <FormHelperText>
                  {t('sendInviteHelp', {
                    context: !!(values as OrgUserCreate).sendInvite ? 'checked' : 'unchecked',
                  })}
                </FormHelperText>
              </Grid>
            )}
          </Grid>
        </div>
        <Stack direction="row" spacing={2}>
          <Box>
            <Button
              color="secondary"
              onClick={() => (!!onCancel ? onCancel() : resetForm())}
              size="large"
              disabled={!!isSubmitting || !dirty}
            >
              {t('cancel')}
            </Button>
          </Box>
          <Box>
            <LoadingButton
              color="secondary"
              size="large"
              variant="contained"
              disabled={!dirty || !isValid}
              loading={isSubmitting}
              type="submit"
            >
              {t(!!initialValues.email ? 'update' : 'create')}
            </LoadingButton>
          </Box>
        </Stack>
      </Stack>
    </Form>
  )
}
